import {match, Pattern} from 'ts-pattern';

import {always} from 'ramda';

import type {Co2EmissionClassEnum} from '@dms/api/vehicle';

import {type Nullish} from 'shared/web';

/**
 * Calculate CO2 emission class (A-G) based on emission value in g/km
 *
 * CO2 class classification:
 * - A: 0 g/km
 * - B: 1-95 g/km
 * - C: 96-115 g/km
 * - D: 116-135 g/km
 * - E: 136-155 g/km
 * - F: 156-175 g/km
 * - G: 176+ g/km
 *
 * @param emissionValue - CO2 emission value in g/km (as string or number)
 * @returns CO2 class (A-G) or null if value is invalid
 */
export function calculateCo2Class(
  emissionValue: string | number | Nullish
): Co2EmissionClassEnum | null {
  const numericValue = match(emissionValue)
    .with(Pattern.nullish, always(null))
    .with(Pattern.string, (value) => {
      const trimmed = value.trim();
      if (trimmed === '') {
        return null;
      }
      return Number(trimmed);
    })
    .with(Pattern.number, (value) => value)
    .otherwise(always(null));

  if (
    numericValue == null ||
    Number.isNaN(numericValue) ||
    !Number.isFinite(numericValue) ||
    numericValue < 0
  ) {
    return null;
  }

  return match<number, Co2EmissionClassEnum>(numericValue)
    .with(Pattern.number.lt(1), () => 'A')
    .with(Pattern.number.lt(96), () => 'B')
    .with(Pattern.number.lt(116), () => 'C')
    .with(Pattern.number.lt(136), () => 'D')
    .with(Pattern.number.lt(156), () => 'E')
    .with(Pattern.number.lt(176), () => 'F')
    .otherwise(() => 'G');
}
